/**
 * Displays the player's current coin count in the HUD.
 * Shows a small coin icon followed by the count.
 *
 * The coin icon periodically plays a flip animation (squash on X
 * axis to simulate a coin spin). When the player collects a coin,
 * a special effect with flip + golden glow is triggered via
 * playCollectEffect().
 */

import { TEX_COIN } from '../utils/constants.js';

const TEXT_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '14px',
  color: '#ffffff',
};

export default class CoinCounter {
  /**
   * @param {Phaser.Scene} scene
   * @param {number} x
   * @param {number} y
   */
  constructor(scene, x, y) {
    this.scene = scene;

    // Golden glow behind the coin icon
    this._glow = scene.add.graphics();
    this._glow.setAlpha(0);
    this._glow.fillStyle(0xb8960c, 0.3);
    this._glow.fillCircle(0, 0, 10);
    this._glow.fillStyle(0xb8960c, 0.12);
    this._glow.fillCircle(0, 0, 14);
    this._glow.setPosition(x + 7, y);

    // Small coin icon
    this.icon = scene.add.sprite(x, y, TEX_COIN);
    this.icon.setOrigin(0, 0.5);
    this.icon.setScale(0.9);

    // Coin count text
    this.text = scene.add.text(x + 18, y, '0', TEXT_STYLE);
    this.text.setOrigin(0, 0.5);

    /** @private Timer reference for the random effect loop. */
    this._effectTimer = null;

    this._scheduleNextEffect();
  }

  /**
   * Update the displayed coin count.
   *
   * @param {number} count
   */
  setCount(count) {
    this.text.setText(`${count}`);
  }

  /** Clean up. */
  destroy() {
    if (this._effectTimer) {
      this._effectTimer.destroy();
      this._effectTimer = null;
    }
    if (this._glow) {
      this._glow.destroy();
      this._glow = null;
    }
    if (this.icon) {
      this.icon.destroy();
      this.icon = null;
    }
    if (this.text) {
      this.text.destroy();
      this.text = null;
    }
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /**
   * Schedule the next random effect (glow or flip) after a random delay.
   * @private
   */
  _scheduleNextEffect() {
    const delay = 4000 + Math.random() * 3000;
    this._effectTimer = this.scene.time.delayedCall(delay, () => {
      this._playRandomEffect();
      this._scheduleNextEffect();
    });
  }

  /**
   * Play the periodic idle effect: coin flip only (no glow).
   * @private
   */
  _playRandomEffect() {
    if (!this.icon || !this.scene) return;
    this._playFlip();
  }

  /**
   * Play the collection effect: coin flip + golden glow together.
   * Called when the player collects a coin.
   */
  playCollectEffect() {
    if (!this.icon || !this.scene) return;
    this._playGlow();
    this._playFlip();
  }

  /**
   * Pulse the golden glow behind the coin icon.
   * @private
   */
  _playGlow() {
    if (!this._glow || !this.scene) return;

    this.scene.tweens.add({
      targets: this._glow,
      alpha: 1,
      duration: 300,
      yoyo: true,
      hold: 100,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        if (this._glow) this._glow.setAlpha(0);
      },
    });
  }

  /**
   * Simulate a coin flip by squashing scaleX to 0 and back (like
   * the coin turning edge-on and back to face). Done twice for a
   * full "flip" feel.
   * @private
   */
  _playFlip() {
    if (!this.icon || !this.scene) return;

    // First half-turn: face → edge
    this.scene.tweens.add({
      targets: this.icon,
      scaleX: 0,
      duration: 120,
      ease: 'Sine.easeIn',
      onComplete: () => {
        if (!this.icon || !this.scene) return;
        // Second half-turn: edge → face
        this.scene.tweens.add({
          targets: this.icon,
          scaleX: 0.9,
          duration: 120,
          ease: 'Sine.easeOut',
          onComplete: () => {
            if (!this.icon || !this.scene) return;
            // Second flip (optional — makes it feel like a full rotation)
            this.scene.tweens.add({
              targets: this.icon,
              scaleX: 0,
              duration: 120,
              ease: 'Sine.easeIn',
              onComplete: () => {
                if (!this.icon || !this.scene) return;
                this.scene.tweens.add({
                  targets: this.icon,
                  scaleX: 0.9,
                  duration: 120,
                  ease: 'Sine.easeOut',
                });
              },
            });
          },
        });
      },
    });
  }
}
